import i18next from 'i18next';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  approvePatientFile,
  completeOwnPatientFileUpload,
  completePatientFileUpload,
  fetchOwnPatientFiles,
  fetchPatientFiles,
  initiateOwnPatientFileUpload,
  initiatePatientFileUpload,
  rejectPatientFile,
  setPatientFileVisibility,
  uploadFileToPresignedUrl,
} from '@/api/files/filesApi';
import type { FileUploadInitPayload, PatientFile } from '@/api/files/filesApi.types';
import {
  WORKSPACE_ATTENTION_QUERY_KEY,
  buildOwnPatientFilesQueryKey,
  buildPatientFilesQueryKey,
} from '@/constants/queryKeys';

export function usePatientFilesQuery(patientId: string): UseQueryResult<PatientFile[], Error> {
  return useQuery({
    queryKey: [...buildPatientFilesQueryKey(patientId), i18next.language],
    queryFn: function loadFiles() {
      return fetchPatientFiles(patientId);
    },
    enabled: patientId.length > 0,
  });
}

interface UploadPatientFileVariables {
  patientId: string;
  file: File;
  visibility?: string;
}

export function useUploadPatientFileMutation(): UseMutationResult<
  PatientFile,
  Error,
  UploadPatientFileVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function upload(variables: UploadPatientFileVariables) {
      const payload: FileUploadInitPayload = {
        filename: variables.file.name,
        contentType: variables.file.type,
        sizeBytes: variables.file.size,
        visibility: variables.visibility,
      };
      const uploadInit = await initiatePatientFileUpload(variables.patientId, payload);
      await uploadFileToPresignedUrl(uploadInit.uploadUrl, variables.file, uploadInit.contentType);
      return completePatientFileUpload(uploadInit.fileId);
    },
    onSuccess: function invalidate(_uploadedFile, variables) {
      queryClient.invalidateQueries({ queryKey: buildPatientFilesQueryKey(variables.patientId) });
    },
  });
}

interface SetFileVisibilityVariables {
  fileId: string;
  patientId: string;
  visibility: string;
}

export function useSetFileVisibilityMutation(): UseMutationResult<
  PatientFile,
  Error,
  SetFileVisibilityVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function update(variables: SetFileVisibilityVariables) {
      return setPatientFileVisibility(variables.fileId, variables.visibility);
    },
    onSuccess: function invalidate(_updatedFile, variables) {
      queryClient.invalidateQueries({ queryKey: buildPatientFilesQueryKey(variables.patientId) });
    },
  });
}

interface FileReviewVariables {
  fileId: string;
  patientId: string;
}

export function useApproveFileMutation(): UseMutationResult<
  PatientFile,
  Error,
  FileReviewVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function approve(variables: FileReviewVariables) {
      return approvePatientFile(variables.fileId);
    },
    onSuccess: function invalidate(_approvedFile, variables) {
      queryClient.invalidateQueries({ queryKey: buildPatientFilesQueryKey(variables.patientId) });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_ATTENTION_QUERY_KEY });
    },
  });
}

interface RejectFileVariables extends FileReviewVariables {
  reviewNote: string | null;
}

export function useRejectFileMutation(): UseMutationResult<
  PatientFile,
  Error,
  RejectFileVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function reject(variables: RejectFileVariables) {
      return rejectPatientFile(variables.fileId, variables.reviewNote);
    },
    onSuccess: function invalidate(_rejectedFile, variables) {
      queryClient.invalidateQueries({ queryKey: buildPatientFilesQueryKey(variables.patientId) });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_ATTENTION_QUERY_KEY });
    },
  });
}

export function useOwnPatientFilesQuery(
  patientId: string
): UseQueryResult<PatientFile[], Error> {
  return useQuery({
    queryKey: [...buildOwnPatientFilesQueryKey(patientId), i18next.language],
    queryFn: function loadFiles() {
      return fetchOwnPatientFiles(patientId);
    },
    enabled: patientId.length > 0,
  });
}

interface UploadOwnFileVariables {
  patientId: string;
  file: File;
  treatmentPlanExerciseId?: string;
}

export function useUploadOwnFileMutation(): UseMutationResult<
  PatientFile,
  Error,
  UploadOwnFileVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function upload(variables: UploadOwnFileVariables) {
      const payload: FileUploadInitPayload = {
        filename: variables.file.name,
        contentType: variables.file.type,
        sizeBytes: variables.file.size,
        treatmentPlanExerciseId: variables.treatmentPlanExerciseId,
      };
      const uploadInit = await initiateOwnPatientFileUpload(variables.patientId, payload);
      await uploadFileToPresignedUrl(uploadInit.uploadUrl, variables.file, uploadInit.contentType);
      return completeOwnPatientFileUpload(uploadInit.fileId);
    },
    onSuccess: function invalidate(_uploadedFile, variables) {
      queryClient.invalidateQueries({
        queryKey: buildOwnPatientFilesQueryKey(variables.patientId),
      });
    },
  });
}
